import { useState } from 'react';

import {
    Button,
    Card,
    cn,
    formatEuro,
    MonoLabel,
    severityDot,
    type Severity,
} from '@/design-system';
import {
    FINDING_SEVERITY_LABELS,
    totalCents,
    URGENCY_LABELS,
    type FindingSeverity,
} from '@/entities/finding';

import { surveyCopy } from '../copy';
import type { PhotoRecord } from '@/shared/offline';

import type { CostBlocks, FindingView } from '../model/view';
import { ManualFindingForm, type ManualFindingDraft } from './ManualFindingForm';

export interface FindingsPanelProps {
    findings: readonly FindingView[];
    /** Hele centen, zoals de engine ze rekent. */
    costs: CostBlocks;
    answered: number;
    visible: number;
    hidden: number;
    /** Handmatige bevindingen toevoegen, wijzigen en verwijderen; weg bij een ingediende opname. */
    manual?: {
        places: readonly { value: string; label: string }[];
        /** Het onderdeel dat nu open staat (knoop-id): de voor de hand liggende plek. */
        defaultPlace: string;
        onSave: (findingUuid: string, draft: ManualFindingDraft) => void;
        onRemove: (findingUuid: string) => void;
        /** Een nieuwe bevinding krijgt haar uuid bij het openen, zodat er meteen foto's bij kunnen. */
        newFindingUuid: () => string;
        photosByFinding: ReadonlyMap<string, readonly PhotoRecord[]>;
        onAddPhotos: (findingUuid: string, files: File[]) => void;
        onRemovePhoto: (clientUuid: string) => void;
        /** Annuleren van een nieuwe bevinding: haar foto's gaan niet mee. */
        onDiscardPhotos: (findingUuid: string) => void;
    };
    className?: string;
}

/** De kleur van een bevinding: rood voor een gebrek, amber voor een aandachtspunt. */
const SEVERITY_TONE: Record<FindingSeverity, Severity> = {
    defect: 'gebrek',
    attention: 'aandacht',
    clean: 'goed',
};

/**
 * De rechterkolom: bevindingen, kostentotalen en het aantal vragen dat dicht
 * bleef. Dat laatste staat er positief: niet-gestelde vragen zijn tijdwinst,
 * geen gat (patterns.md §4).
 */
export function FindingsPanel({
    findings,
    costs,
    answered,
    visible,
    hidden,
    manual,
    className,
}: FindingsPanelProps) {
    /** De bevinding die openstaat: een nieuwe (met haar uuid al gemaakt) of een bestaande. */
    const [editing, setEditing] = useState<{ findingUuid: string; isNew: boolean } | null>(null);
    const editedFinding = editing?.isNew
        ? undefined
        : findings.find(
              (finding) =>
                  finding.source === 'manual' && finding.findingUuid === editing?.findingUuid,
          );

    return (
        <aside
            aria-label={surveyCopy.panels.findings}
            className={cn(
                'flex flex-col gap-[14px] overflow-auto border-l border-[var(--border-default)] bg-[var(--surface-card)] px-4 pt-4 pb-8',
                className,
            )}
        >
            <section>
                <h2 className="m-0 mb-1 font-[family-name:var(--font-display)] text-[15px] font-semibold text-[var(--text-primary)]">
                    {surveyCopy.findings.title}
                </h2>
                <p className="m-0 mb-3 text-[12px] text-[var(--text-tertiary-safe)]">
                    {surveyCopy.findings.summary(findings.length, answered, visible)}
                </p>

                {manual && editing === null ? (
                    <Button
                        variant="outline"
                        size="md"
                        fullWidth
                        className="mb-3"
                        onClick={() =>
                            setEditing({ findingUuid: manual.newFindingUuid(), isNew: true })
                        }
                    >
                        {surveyCopy.manual.add}
                    </Button>
                ) : null}

                {manual && editing && (editing.isNew || editedFinding) ? (
                    <div className="mb-3">
                        <ManualFindingForm
                            key={editing.findingUuid}
                            places={manual.places}
                            isNew={editing.isNew}
                            photos={{
                                items: manual.photosByFinding.get(editing.findingUuid) ?? [],
                                onAdd: (files) => manual.onAddPhotos(editing.findingUuid, files),
                                onRemove: manual.onRemovePhoto,
                            }}
                            {...(editedFinding ? { placeLabel: editedFinding.place } : {})}
                            initial={
                                editedFinding
                                    ? {
                                          place: editedFinding.nodeId ?? '',
                                          description: editedFinding.label,
                                          location: editedFinding.location,
                                          severity:
                                              editedFinding.severity === 'defect'
                                                  ? 'defect'
                                                  : 'attention',
                                          costs: editedFinding.costs,
                                      }
                                    : {
                                          place: manual.defaultPlace,
                                          description: '',
                                          location: '',
                                          severity: 'defect',
                                          costs: { direct: 0, term: 0, improvement: 0 },
                                      }
                            }
                            onSave={(draft) => {
                                manual.onSave(editing.findingUuid, draft);
                                setEditing(null);
                            }}
                            {...(editedFinding?.findingUuid
                                ? {
                                      onRemove: () => {
                                          manual.onRemove(editedFinding.findingUuid ?? '');
                                          setEditing(null);
                                      },
                                  }
                                : {})}
                            onCancel={() => {
                                if (editing.isNew) {
                                    manual.onDiscardPhotos(editing.findingUuid);
                                }

                                setEditing(null);
                            }}
                        />
                    </div>
                ) : null}

                {findings.length === 0 ? (
                    <p className="m-0 text-[13px] leading-[1.5] text-[var(--text-tertiary-safe)]">
                        {surveyCopy.findings.empty}
                    </p>
                ) : (
                    <ul className="m-0 flex list-none flex-col gap-2 p-0">
                        {findings.map((finding) => {
                            const content = (
                                <>
                                    <span
                                        aria-hidden="true"
                                        className="mt-[5px] h-2 w-2 shrink-0 rounded-full"
                                        style={{
                                            background:
                                                severityDot[SEVERITY_TONE[finding.severity]],
                                        }}
                                    />
                                    <div className="min-w-0 flex-1">
                                        <p className="m-0 text-[13px] leading-[1.3] font-medium text-[var(--text-primary)]">
                                            {finding.label}
                                        </p>
                                        <p className="m-0 mt-[3px] text-[11px] text-[var(--text-tertiary-safe)]">
                                            {[
                                                finding.severity === 'clean'
                                                    ? null
                                                    : FINDING_SEVERITY_LABELS[finding.severity],
                                                finding.urgency
                                                    ? URGENCY_LABELS[finding.urgency]
                                                    : null,
                                                finding.place,
                                                finding.source === 'manual'
                                                    ? surveyCopy.manual.marker
                                                    : null,
                                            ]
                                                .filter(Boolean)
                                                .join(' · ')}
                                        </p>
                                    </div>
                                    <MonoLabel size={11} variant="value" tone="default">
                                        {euro(totalCents(finding.costs))}
                                    </MonoLabel>
                                </>
                            );

                            return (
                                <li key={finding.key}>
                                    {manual && finding.source === 'manual' ? (
                                        <button
                                            type="button"
                                            aria-label={surveyCopy.manual.edit(finding.label)}
                                            onClick={() =>
                                                finding.findingUuid
                                                    ? setEditing({
                                                          findingUuid: finding.findingUuid,
                                                          isNew: false,
                                                      })
                                                    : undefined
                                            }
                                            className="flex min-h-[44px] w-full cursor-pointer items-start gap-[10px] rounded-[9px] border border-[var(--border-default)] bg-[var(--surface-card)] px-3 py-[11px] text-left font-[family-name:var(--font-body)] outline-offset-2 hover:border-[var(--accent)] focus-visible:outline-2 focus-visible:outline-[var(--accent)]"
                                        >
                                            {content}
                                        </button>
                                    ) : (
                                        <div className="flex items-start gap-[10px] rounded-[9px] border border-[var(--border-default)] px-3 py-[11px]">
                                            {content}
                                        </div>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                )}
            </section>

            <section className="border-t border-[var(--border-subtle)] pt-[14px]">
                <CostRow label={surveyCopy.findings.costDirect} amount={costs.direct} />
                <CostRow label={surveyCopy.findings.costTerm} amount={costs.term} />
                {costs.improvement > 0 ? (
                    <CostRow
                        label={surveyCopy.findings.costImprovement}
                        amount={costs.improvement}
                    />
                ) : null}
                <div className="flex justify-between border-t border-[var(--border-subtle)] pt-[9px] text-[14px]">
                    <span className="font-medium text-[var(--text-primary)]">
                        {surveyCopy.findings.costTotal}
                    </span>
                    <span className="font-[family-name:var(--font-display)] font-semibold text-[var(--text-primary)]">
                        {euro(costs.total)}
                    </span>
                </div>
            </section>

            <Card variant="dark" padding="md">
                <MonoLabel size={10} tone="onDarkMuted" as="div" className="mb-2">
                    {surveyCopy.findings.notAskedTitle}
                </MonoLabel>
                <p className="m-0 text-[13px] leading-[1.5] text-[var(--text-on-dark-2)]">
                    {surveyCopy.findings.notAsked(hidden)}
                </p>
            </Card>
        </aside>
    );
}

function CostRow({ label, amount }: { label: string; amount: number }) {
    return (
        <div className="mb-[7px] flex justify-between text-[13px]">
            <span className="text-[var(--text-tertiary-safe)]">{label}</span>
            <span className="font-medium text-[var(--text-primary)]">{euro(amount)}</span>
        </div>
    );
}

/** De engine rekent in hele centen; het scherm toont hele euro's. */
function euro(cents: number): string {
    return formatEuro(Math.round(cents / 100));
}
